import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FeatureIdentify, FeatureRecognizeResult } from '../feature-identify';
import { Emitter, Logger, type Event } from '@patsnap/tahiti-core';
import { NoveltyCheckTaskStatusService } from '../task-status-service';
import { TechnicalFeaturesGetApi, type TechnicalFeaturesGetApiResponse } from '@/infrastructure/api-functions/core-drafting-api/technical-features-get-api';
import { RetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { sleep } from '@telecom/shared/utils/task';

/**
 * 技术特征获取轮询延迟时间（毫秒）
 */
const TECH_FEATURES_POLLING_DELAY = 1000;

/**
 * 技术特征获取任务状态常量
 */
const TECH_FEATURES_STATUS = {
    RUNNING: 'RUNNING' as const,
    SUCCESS: 'SUCCESS' as const,
    FAILED: 'FAILED' as const,
};

@injectable()
export class FeatureIdentifyImpl implements FeatureIdentify {
    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(TechnicalFeaturesGetApi)
        private readonly _technicalFeaturesGetApi: TechnicalFeaturesGetApi,
        @inject(NoveltyCheckTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: NoveltyCheckTaskStatusService,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
    ) { }

    private _onFeatureRecognitionLoadingEmitter: Emitter<boolean> = new Emitter();

    readonly onFeatureRecognitionLoading: Event<boolean> = this._onFeatureRecognitionLoadingEmitter.event;

    async recognize(taskId: string): Promise<FeatureRecognizeResult> {
        this._onFeatureRecognitionLoadingEmitter.fire(true);
        const result = await this._pullTechFeatures(taskId)
            .finally(() => {
                this._onFeatureRecognitionLoadingEmitter.fire(false);
            });
        return result;
    }

    stop(): void {
        this._logger.info('特征识别服务.stop');
        this._abortController?.abort();
        // 刷新任务状态
        this._noveltyCheckTaskStatusService.pullTaskStatus();
    }

    private _abortController: AbortController | undefined;

    async _pullTechFeatures(taskId: string): Promise<FeatureRecognizeResult> {
        this._abortController = new AbortController();
        let isFinished = false;
        let apiResponse: TechnicalFeaturesGetApiResponse = { features: [] };

        try {
            while (!isFinished) {
                const response = await this._technicalFeaturesGetApi.getTechnicalFeatures({
                    task_id: taskId,
                }, this._abortController.signal);

                if (response.status === TECH_FEATURES_STATUS.SUCCESS) {
                    apiResponse = response.result || { features: [] };
                    isFinished = true;
                } else if (response.status === TECH_FEATURES_STATUS.FAILED) {
                    throw new Error(response.error_message || '技术特征获取失败');
                } else if (response.status === TECH_FEATURES_STATUS.RUNNING) {
                    // 继续轮询，等待1秒后重试
                    await sleep(TECH_FEATURES_POLLING_DELAY);
                }
            }
        } finally {
            this._abortController = undefined;
        }

        const result = this._convertToFeatureRecognizeResult(apiResponse);
        this._updateFeatureRecognizeResultToModel(result);
        return result;
    }

    private _convertToFeatureRecognizeResult(res: TechnicalFeaturesGetApiResponse): FeatureRecognizeResult {
        return {
            techFeatures: res.features
                .map((feature) => ({
                    name: feature.feature_text_original,
                    reason: feature.feature_weight_reason,
                    selected: feature.selected,
                    flagged: feature.feature_weight === 'core' || feature.feature_weight === 'topic',
                    manualAdded: !!feature.user_add,
                    featureWeight: feature.feature_weight,
                    originData: feature,
                    components: feature.components ?? [],
                })),
            selectedImages: res.selected_images,
        };
    }

    /**
     * 将技术特征识别结果更新到模型
     */
    private _updateFeatureRecognizeResultToModel(result: FeatureRecognizeResult) {
        this._retrievalResultReportModel.update((state) => {
            state.techFeatures = result.techFeatures;
            state.selectedImages = result.selectedImages;
            return state;
        });
    }
}
